import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, Link2, List, ListOrdered } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AddVariableMenu } from './add-variable-menu';

interface RichVariableEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

// Rich-text editor for pre-boarding email / welcome copy: the marks the tickets
// ask for (bold/italic/lists/links) plus an "Add Variable" insert that drops a
// literal {{token}} at the cursor. Shared across wizard steps.
export function RichVariableEditor({
  content,
  onChange,
  placeholder = 'Write something…',
}: RichVariableEditorProps) {
  // Force re-render on selection changes so toolbar buttons reflect active marks.
  const [, forceRender] = useState({});

  const editor = useEditor({
    extensions: [
      // StarterKit v3 bundles its own Link; disable it so our configured Link
      // (no open-on-click, underlined) is the only one — avoids a duplicate warning.
      StarterKit.configure({ heading: false, link: false }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-primary underline' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: content || '',
    editable: true,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    onSelectionUpdate: () => forceRender({}),
    editorProps: {
      attributes: {
        class:
          'prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[160px] p-4 text-sm',
      },
    },
  });

  // Sync external content changes (e.g. switching forms / draft reload).
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== content) {
      editor.commands.setContent(content || '', { emitUpdate: false });
    }
  }, [content, editor]);

  return (
    <div className="rounded-lg border border-border bg-background overflow-hidden">
      <Toolbar editor={editor} />
      <div className="max-h-[360px] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor | null }) {
  if (!editor) return null;

  const addLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Enter URL:', previous ?? '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertVariable = (token: string) => {
    editor.chain().focus().insertContent(`{{${token}}}`).run();
  };

  return (
    <div className="flex items-center gap-1 border-b border-border p-2">
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="Bold (⌘B)"
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="Italic (⌘I)"
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <Divider />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>
      <Divider />
      <ToolbarButton onClick={addLink} active={editor.isActive('link')} title="Insert Link (⌘K)">
        <Link2 className="h-4 w-4" />
      </ToolbarButton>
      <div className="ml-auto">
        <AddVariableMenu compact onInsert={insertVariable} />
      </div>
    </div>
  );
}

function ToolbarButton({
  onClick,
  active,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      title={title}
      className={cn('h-8 w-8 p-0', active && 'bg-muted')}
    >
      {children}
    </Button>
  );
}

function Divider() {
  return <div className="h-6 w-px bg-border mx-1" />;
}
